import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  buildRetailItemNumberOptions,
  getLinkedRetailItemDefault,
  linkedRetailItemOptionValue,
  parseRetailItemNumberFromOption,
} from "../../../constants/DefaultRetailItems";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";
const YES_NO = ["Yes", "No"];

/** Same rule as SpaServices: rows with sample_test_agilysys in any string value are demo data. */
const isSampleSpaServiceRow = (obj: any): boolean =>
  Object.values(obj ?? {}).some((value: any) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes("sample_test_agilysys");
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes("sample_test_agilysys"),
      );
    }
    return false;
  });

const SPA_ADD_ON_FIELD_RETAIL_ITEM_NUMBER = "Retail Item Number";
const SPA_ADD_ON_FIELD_RETAIL_ITEM_NUMBER_LEGACY = "Linked Service";

const SpaAddOns = ({
  data,
  fieldValues,
  spaRetailCategories,
  spaRetailItems,
}: {
  data: (value: any) => void;
  fieldValues: any;
  spaRetailCategories?: any[] | null;
  spaRetailItems?: any[] | null;
}) => {
  const [addOnName, setAddOnName] = useState<string | null>(null);
  const [commissionable, setCommissionable] = useState<string>("No");
  const [retailCategory, setRetailCategory] = useState<string | null>(null);
  const [price, setPrice] = useState<number | "">("");
  const [availableOnWeb, setAvailableOnWeb] = useState<boolean>(false);
  const [retailItemNumber, setRetailItemNumber] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [addOnNameError, setAddOnNameError] = useState<boolean>(false);
  const [addOnNameErrorMessage, setAddOnNameErrorMessage] =
    useState<string>("");
  const [retailCategoryError, setRetailCategoryError] =
    useState<boolean>(false);
  const [priceError, setPriceError] = useState<boolean>(false);
  const [retailItemNumberError, setRetailItemNumberError] =
    useState<boolean>(false);

  const retailCategoryOptions = useMemo(
    () =>
      removeSampleTestArray(
        fillAutocompleteValues(
          (spaRetailCategories ?? []).filter(
            (row: any) => !isSampleSpaServiceRow(row),
          ),
          DNDFieldsEnum.CATEGORYNAME,
          "",
        ) ?? [],
      ),
    [spaRetailCategories],
  );

  const retailItemNumberOptions = useMemo(
    () => buildRetailItemNumberOptions(spaRetailItems ?? []),
    [spaRetailItems],
  );

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items ?? []);
    setSelectedId(checkData(items ?? [], selectedId) ? selectedId : null);
  }, [items]);

  const validateAddOnFields = (): boolean => {
    if (!addOnName || addOnName.length === 0) {
      setAlertMessage("Add On Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setAddOnNameError(false);
    setAddOnNameErrorMessage("");
    if (
      !validateString(
        addOnName,
        validationLength.addOnName,
        validationType.NAME,
      )
    ) {
      setAddOnNameError(true);
      setAddOnNameErrorMessage(validationMessages.addOnName);
      return false;
    }

    const listForDuplicate = selectedId
      ? (items ?? []).filter((i: any) => String(i.id) !== String(selectedId))
      : (items ?? []);
    if (checkDuplicate(listForDuplicate, DNDFieldsEnum.ADDONNAME, addOnName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (!retailCategory || retailCategory.trim().length === 0) {
      setAlertMessage("Retail Category is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      setRetailCategoryError(true);
      return false;
    }
    setRetailCategoryError(false);

    if (price === "" || price === undefined) {
      setAlertMessage("Price is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      setPriceError(true);
      return false;
    }
    setPriceError(false);

    if (!retailItemNumber || String(retailItemNumber).trim().length === 0) {
      setAlertMessage("Retail item number is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      setRetailItemNumberError(true);
      return false;
    }
    setRetailItemNumberError(false);

    return true;
  };

  const updateItem = () => {
    if (!validateAddOnFields()) {
      return false;
    }
    setItems((prev: any) =>
      (prev ?? []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.addOns} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateAddOnFields()) {
      return false;
    }
    setItems((prev: any) =>
      updateData(
        prev?.length
          ? [...(prev ?? []), { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setAddOnName(list["Add On Name"] ?? null);
      setCommissionable(list?.Commissionable ?? "No");
      setRetailCategory(list["Retail Category"] ?? null);
      setPrice(
        list?.Price !== undefined && list?.Price !== null
          ? Number(list.Price)
          : "",
      );
      setAvailableOnWeb(parseSpaBooleanField(list["Available on Web"], false));
      const itemNumber =
        list[SPA_ADD_ON_FIELD_RETAIL_ITEM_NUMBER] ??
        list[SPA_ADD_ON_FIELD_RETAIL_ITEM_NUMBER_LEGACY];
      setRetailItemNumber(
        itemNumber != null && itemNumber !== ""
          ? String(itemNumber)
          : getLinkedRetailItemDefault(retailItemNumberOptions),
      );
    } else {
      setAddOnName(null);
      setCommissionable("No");
      setRetailCategory(null);
      setPrice("");
      setAvailableOnWeb(false);
      setRetailItemNumber(getLinkedRetailItemDefault(retailItemNumberOptions));
      setAddOnNameError(false);
      setAddOnNameErrorMessage("");
      setRetailCategoryError(false);
      setPriceError(false);
      setRetailItemNumberError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const payload = {
    "Add On Name": addOnName,
    Commissionable: commissionable,
    "Retail Category": retailCategory,
    Price: price === "" ? undefined : Number(price),
    "Available on Web": availableOnWeb ? "Yes" : "No",
    [SPA_ADD_ON_FIELD_RETAIL_ITEM_NUMBER]: retailItemNumber,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Add Ons
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.addOnHeader ?? "Add Ons",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="10px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Add On Name *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.addOnServiceAddOnName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              value={addOnName ?? ""}
              onChange={(e) => setAddOnName(e.target.value || null)}
              helperText={addOnNameError ? addOnNameErrorMessage : ""}
              error={addOnNameError}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Commissionable</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.addOnCommissionable)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              options={YES_NO}
              value={commissionable}
              onChange={(_, v) => setCommissionable(v ?? "No")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Retail Category *</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.addOnRetailCategory)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              size="small"
              options={retailCategoryOptions}
              value={retailCategory}
              disabled={!retailCategoryOptions.length}
              onChange={(_, v) => {
                setRetailCategory(v);
                setRetailCategoryError(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    retailCategoryOptions.length
                      ? "Select retail category"
                      : "Add retail categories first"
                  }
                  error={retailCategoryError}
                  helperText={
                    retailCategoryError ? validationMessages.fieldRequired : ""
                  }
                />
              )}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Price *</FormLabel>
              <Tooltip title={tooltipComponent(spaTooltipHeaders.addOnPrice)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              type="number"
              inputProps={{ step: 0.01 }}
              variant="outlined"
              size="small"
              value={price}
              error={priceError}
              helperText={priceError ? validationMessages.fieldRequired : ""}
              onChange={(e) => {
                setPrice(
                  e.target.value === "" ? "" : parseFloat(e.target.value) || "",
                );
                setPriceError(false);
              }}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Retail Item Number *</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.addOnLinkedItem)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              size="small"
              options={retailItemNumberOptions}
              value={linkedRetailItemOptionValue(
                retailItemNumber,
                retailItemNumberOptions,
              )}
              disabled={!retailItemNumberOptions.length}
              onChange={(_, v) => {
                setRetailItemNumber(parseRetailItemNumberFromOption(v));
                setRetailItemNumberError(false);
              }}
              isOptionEqualToValue={(a, b) => a === b}
              noOptionsText="No retail items available"
              openOnFocus
              selectOnFocus
              autoHighlight
              clearOnEscape
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    retailItemNumberOptions.length
                      ? "Search retail items..."
                      : "Add retail items first"
                  }
                  error={retailItemNumberError}
                  helperText={
                    retailItemNumberError
                      ? validationMessages.fieldRequired
                      : ""
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={availableOnWeb}
                  onChange={() => setAvailableOnWeb(!availableOnWeb)}
                />
              }
              label={
                <Stack direction="row" alignItems="center">
                  Available on Web
                  <Tooltip
                    title={tooltipComponent(
                      spaTooltipHeaders.addOnAvailableOnWeb,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack>
              }
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Add On
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Add Ons Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={(e) => editItem(e)}
              onDelete={(e) => deleteItemById(e)}
              code={DNDFieldsEnum.ADDONNAME}
              name={DNDFieldsEnum.PRICE}
            />
          ) : (
            <Typography variant="body2">No Add Ons Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaAddOns;
